import React, { useEffect, useRef, useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { StepGatoComingSoon } from '../../steps/StepGato_ComingSoon';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { AnimalType } from '../../types';
import { capitalize } from '../../utils/strings';
import { useAddonMode } from '../../hooks/useAddonMode';

const P1Welcome: React.FC = () => {
  const { formData, updateFormData, goToNextStep, petsCompleted, multiPetIntent, setMultiPetIntent } = useForm();
  const { t, config, platform } = useAppConfig();
  const addon = useAddonMode();

  const dogImage = config.funnel_config.translations?.['step1.image.dog'] || '/images/funnel/dog-illustration.png';
  const catImage = config.funnel_config.translations?.['step1.image.cat'] || '/images/funnel/cat-illustration.png';
  const title = t('p1.title', 'Crea el menú personalizado para tu peludo');
  // Mobile variant of the title: rendered only when the admin sets a different text.
  const titleMobile = t('p1.title_mobile', 'Crea el menú personalizado para tu peludo');
  // Second paragraph of the vet-team pop-up: shown only when the admin fills it in.
  const vetTeamSecondParagraph = t('p1.vet_team_body_2', '');

  // P1 lazy-inits from formData so back-nav restores the user's previous
  // selection. On a fresh visit formData.animalType is undefined → null,
  // so the CTA stays disabled until a conscious selection is made.
  const [animalType, setAnimalType] = useState<AnimalType | null>(formData.animalType ?? null);
  const [nombre, setNombre] = useState(formData.nombre || '');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [attempted, setAttempted] = useState(false);
  const [showMultiPetInfo, setShowMultiPetInfo] = useState(false);
  const [showVetTeam, setShowVetTeam] = useState(false);

  // En modo addon (?addon=<subId> en la URL), asumimos perro automáticamente
  // y enfocamos el input — el cliente solo necesita escribir el nombre del
  // peludo nuevo. Skip de la pregunta de animal.
  useEffect(() => {
    if (addon.isAddon && animalType !== AnimalType.PERRO) {
      setAnimalType(AnimalType.PERRO);
      updateFormData({ animalType: AnimalType.PERRO });
      // setTimeout 0 para esperar a que React renderice el input antes de
      // intentar focus — sin esto, el ref puede ser null en el primer render.
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addon.isAddon]);

  const gatoSelected = animalType === AnimalType.GATO;
  const canContinue = !gatoSelected && animalType !== null && nombre.trim().length > 0;

  const handlePickAnimal = (a: AnimalType) => {
    setAnimalType(a);
    updateFormData({ animalType: a });
    // Tras seleccionar perro, ahorra un tap llevando el foco al input de
    // nombre — abre teclado en móvil sin que el usuario tenga que tocarlo.
    // Solo lo hacemos si no hay nombre todavía y para perro (gato lleva a
    // ComingSoon, no tiene input). Llamamos focus() dentro del click
    // handler para mantener la cadena de gesto que iOS Safari exige.
    if (a === AnimalType.PERRO && !nombre.trim()) {
      nameInputRef.current?.focus();
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;
    goToNextStep();
  };

  const handleMultiPetYes = () => {
    setMultiPetIntent(true);
    setShowMultiPetInfo(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canContinue) handleContinue();
  };

  if (gatoSelected) {
    // StepGato tiene su propio chrome (header + bottom nav + h-screen),
    // así que NO lo envolvemos en FunnelLayout — eso producía un
    // "dispositivo dentro de otro" según Santiago. Renderizamos top-level.
    return (
      <StepGatoComingSoon
        onBack={() => {
          setAnimalType(null);
          updateFormData({ animalType: undefined });
        }}
      />
    );
  }

  const missingReason = !animalType
    ? t('p1.missing_animal', 'Selecciona la especie de tu mascota')
    : !nombre.trim()
      ? t('p1.missing_name', 'Escribe el nombre para continuar')
      : undefined;

  return (
    <FunnelLayout
      step={1}
      useTwoPhase
      onContinue={handleContinue}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      showBack={false}
    >
      {/* Subtle radial-gradient background layer */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(144,209,232,0.18) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="flex flex-col gap-5 relative">
        {/* Bounce keyframes injected once via a style tag (used by greeting line) */}
        <style>{`
          @keyframes p1-bounce {
            0%,100% { transform: translateY(0); }
            45%      { transform: translateY(-8px); }
            65%      { transform: translateY(-4px); }
          }
          @keyframes p1-slidefade {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* ── Banner addon (solo si ?addon=<subId>) ─────────────────────── */}
        {addon.isAddon && (
          <div className="bg-[#008BBC]/10 border border-[#008BBC]/30 rounded-2xl px-4 py-3 text-center">
            <p className="font-fredoka text-sm font-semibold text-[#008BBC]">
              🐾 {t('p1.addon_banner', 'Añadiendo otro peludo a tu plan')}
            </p>
            <p className="font-poppins text-xs text-black/60 mt-1 leading-snug">
              {t('p1.addon_subtitle', 'Solo necesitamos saber del nuevo perro — el resto se hereda de tu cuenta.')}
            </p>
          </div>
        )}

        {/* ── H1 gradient title ────────────────────────────────────────── */}
        <div className="text-center space-y-1">
          <h1
            className="font-fredoka text-3xl sm:text-4xl font-bold leading-tight"
            style={{
              background: 'linear-gradient(135deg, #005f7a 0%, #008BBC 55%, #90D1E8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {addon.isAddon ? (
              t('p1.title_addon', '¿Cómo se llama el nuevo peludo?')
            ) : titleMobile !== title ? (
              <>
                <span className="sm:hidden">{titleMobile}</span>
                <span className="hidden sm:inline">{title}</span>
              </>
            ) : (
              title
            )}
          </h1>
          {!addon.isAddon && (
            <p className="font-poppins text-sm text-black/60">{t('p1.time', 'Te llevará 1 minuto')}</p>
          )}
        </div>

        {/* ── Species cards (oculto en addon mode) ─────────────────────── */}
        {!addon.isAddon && (
          <div
            className={`grid grid-cols-2 gap-4 rounded-[22px] transition-all ${
              attempted && animalType === null ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-1' : ''
            }`}
          >
            {/* Perro */}
            <button
              type="button"
              onClick={() => handlePickAnimal(AnimalType.PERRO)}
              aria-label="Perro"
              aria-pressed={animalType === AnimalType.PERRO}
              className={`relative rounded-[20px] overflow-hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/70
              ${
                animalType === AnimalType.PERRO
                  ? 'border-[3px] border-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)] scale-[1.02]'
                  : 'border-[2.5px] border-transparent hover:scale-[1.02] hover:shadow-lg'
              }`}
            >
              <div className="bg-[#FAF7F1] h-28 sm:h-36 flex flex-col items-center justify-end">
                <img src={dogImage} alt={t('step1.dog_label', 'Perro')} className="w-full h-full object-contain" />
              </div>
              <div className="bg-[#FAF7F1] pb-3 pt-1 flex flex-col items-center">
                <span className="font-fredoka text-base font-semibold text-black">{t('step1.dog_label', 'Perro')}</span>
              </div>
              {/* Selected checkmark badge */}
              {animalType === AnimalType.PERRO && (
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              )}
            </button>

            {/* Gato — visualmente más discreto: en gris/sepia y con label
              "Próximamente". Al tocarlo lleva al ComingSoon (formulario
              de aviso). Antes parecía una opción seleccionable a la par
              que Perro y eso empujaba clicks accidentales que dejaban al
              usuario atrapado en una pantalla "Coming soon" sin contexto. */}
            <button
              type="button"
              onClick={() => handlePickAnimal(AnimalType.GATO)}
              aria-label={t('step1.cat_aria', 'Gato — próximamente')}
              aria-pressed={(animalType as AnimalType | null) === AnimalType.GATO}
              className={`relative rounded-[20px] overflow-hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/70 opacity-70 grayscale-[40%]
              ${animalType === AnimalType.PERRO ? 'opacity-30' : 'hover:opacity-90'}
              border-[2.5px] border-transparent hover:scale-[1.02] hover:shadow-md`}
            >
              <div className="bg-[#FAF7F1] h-28 sm:h-36 flex flex-col items-center justify-end">
                <img src={catImage} alt={t('step1.cat_label', 'Gato')} className="w-full h-full object-contain" />
              </div>
              <div className="bg-[#FAF7F1] pb-3 pt-1 flex flex-col items-center">
                <span className="font-fredoka text-sm font-medium text-black/70">
                  {t('step1.cat_coming_soon', 'Gato · Próximamente')}
                </span>
              </div>
            </button>
          </div>
        )}

        {/* ── Name input ────────────────────────────────────────────────── */}
        <div className="space-y-2">
          <label className="font-fredoka text-base sm:text-lg font-medium text-black text-center block">
            {t('step1.name_label', '¿Cómo se llama tu peludo?')}
          </label>
          <input
            ref={nameInputRef}
            type="text"
            value={nombre}
            maxLength={40}
            onChange={(e) => {
              const cap = capitalize(e.target.value);
              setNombre(cap);
              updateFormData({ nombre: cap });
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('step1.name_placeholder', 'ej: Max, Bella, Milo...')}
            autoComplete="off"
            autoCapitalize="words"
            className={`w-full px-5 py-4 bg-gray-100 rounded-2xl font-fredoka text-xl text-center text-black placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/60 focus:bg-white transition-all border-2 ${
              attempted && !nombre.trim() ? 'border-red-500' : 'border-transparent'
            }`}
          />
          {/* Greeting — slide-up fade-in on name entry */}
          <div
            className="flex items-center justify-center gap-1.5 transition-all duration-300"
            style={
              nombre.trim()
                ? { opacity: 1, animation: 'p1-slidefade 0.35s ease-out' }
                : { opacity: 0, pointerEvents: 'none' }
            }
            aria-live="polite"
          >
            {nombre.trim() && (
              <>
                <span
                  className="text-lg"
                  style={{ animation: 'p1-bounce 1.8s ease-in-out infinite' }}
                  aria-hidden="true"
                >
                  🐾
                </span>
                <p className="font-fredoka text-sm text-[#008BBC]">
                  {t('step1.name_greeting', '¡Encantado, {nombre}! Vamos a preparar tu plan', {
                    nombre: capitalize(nombre.trim()),
                  })}
                </p>
              </>
            )}
          </div>
        </div>

        {/* ── Multi-mascota desactivado por admin: aviso de repetir el test ── */}
        {!addon.isAddon &&
          petsCompleted.length === 0 &&
          platform !== 'prestashop' &&
          config.multipet_rules?.enabled === false && (
            <p className="font-poppins text-xs text-black/50 text-center">
              {t(
                'p1.multipet_disabled_note',
                '¿Tienes más de una mascota? Al terminar, repite el test para crear su plan: cada peludo tendrá su propia suscripción.',
              )}
            </p>
          )}

        {/* ── Selector multi-mascota (solo Shopify, primera mascota, no addon) ── */}
        {!addon.isAddon &&
          petsCompleted.length === 0 &&
          platform !== 'prestashop' &&
          config.multipet_rules?.enabled !== false && (
            <div className="space-y-2">
              <p className="font-poppins text-xs text-black/50 text-center">
                {t('p1.multipet_question', '¿Tienes más de un peludito?')}
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => setMultiPetIntent(false)}
                  aria-pressed={multiPetIntent === false}
                  className={`px-4 py-1.5 rounded-full border-2 font-fredoka text-sm transition-all ${
                    multiPetIntent === false
                      ? 'bg-[#008BBC] border-[#008BBC] text-white'
                      : 'border-[#008BBC]/40 text-black/60 hover:border-[#008BBC]/70'
                  }`}
                >
                  {t('p1.multipet_no', 'Solo una')}
                </button>
                <button
                  type="button"
                  onClick={handleMultiPetYes}
                  aria-pressed={multiPetIntent === true}
                  className={`px-4 py-1.5 rounded-full border-2 font-fredoka text-sm transition-all ${
                    multiPetIntent === true
                      ? 'bg-[#008BBC] border-[#008BBC] text-white'
                      : 'border-[#008BBC]/40 text-black/60 hover:border-[#008BBC]/70'
                  }`}
                >
                  {t('p1.multipet_yes', 'Sí, tengo varias')}
                </button>
              </div>
            </div>
          )}
      </div>

      <div className="mt-3 flex justify-center">
        <button
          type="button"
          onClick={() => setShowVetTeam(true)}
          className="grid w-full max-w-[360px] grid-cols-[auto_1fr] items-center gap-2.5 rounded-2xl border border-[#008BBC]/15 bg-white px-3 py-2.5 text-left shadow-sm transition-all hover:border-[#008BBC]/30 hover:shadow-md"
        >
          <span className="relative h-10 w-[58px] shrink-0" aria-hidden="true">
            <span className="absolute left-7 top-1.5 h-8 w-8 rounded-full bg-[#FFF3C4] ring-2 ring-white" />
            <span className="absolute left-4 top-1 h-9 w-9 rounded-full bg-[#E8F6FA] ring-2 ring-white" />
            <span className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white ring-2 ring-white shadow-sm">
              <img
                src="/images/funnel/vet-avatar.jpg"
                alt=""
                loading="lazy"
                className="h-full w-full scale-[1.35] object-cover object-[55%_18%]"
              />
              <span className="pointer-events-none absolute inset-0 bg-white/10" />
            </span>
          </span>
          <span className="min-w-0">
            <span className="block font-fredoka text-sm font-bold leading-tight text-[#1a1a1a]">
              {t('p1.trust_label', 'Formulado por veterinarios nutricionistas')}
            </span>
          </span>
        </button>
      </div>

      {showMultiPetInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="p1-multipet-info-title"
          onClick={() => setShowMultiPetInfo(false)}
        >
          <div
            className="w-full max-w-sm rounded-[24px] bg-white px-5 py-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative mx-auto mb-4 h-20 w-20">
              <img
                src="/images/funnel/dog-portrait.png"
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="h-full w-full rounded-full border-4 border-[#FAEDE1] bg-[#FAEDE1] object-cover shadow-sm"
              />
              <span
                className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#E8F6FA] text-lg shadow-sm"
                aria-hidden="true"
              >
                🐾
              </span>
            </div>
            <h2
              id="p1-multipet-info-title"
              className="text-center font-fredoka text-2xl font-semibold leading-tight text-[#005f7a]"
            >
              {t('p1.multipet_modal_title', 'Vamos una mascota cada vez')}
            </h2>
            <p className="mt-3 text-center font-poppins text-sm leading-relaxed text-black/70">
              {t(
                'p1.multipet_modal_body',
                'Empezamos con esta mascota. Al terminar su plan, podrás añadir otra antes de pagar, sin repetir pasos innecesarios.',
              )}
            </p>
            <div className="mt-4 rounded-2xl bg-[#F7F4EE] px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-sm" aria-hidden="true">
                  ✓
                </span>
                <p className="font-poppins text-sm leading-snug text-black/65">
                  {t('p1.multipet_modal_note', 'Cada mascota podrá tener sus propias recetas y suplementos.')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMultiPetInfo(false)}
              className="mt-5 w-full rounded-full bg-[#008BBC] px-5 py-3 font-fredoka text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#0077a3] focus:outline-none focus:ring-2 focus:ring-[#008BBC]/60 focus:ring-offset-2"
            >
              {t('p1.multipet_modal_cta', 'Perfecto, seguimos')}
            </button>
          </div>
        </div>
      )}

      {showVetTeam && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="p1-vet-team-title"
          onClick={() => setShowVetTeam(false)}
        >
          <div
            className="w-full max-w-sm rounded-[24px] bg-white px-5 py-5 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-white ring-4 ring-[#E8F6FA]">
              <img
                src="/images/funnel/vet-avatar.jpg"
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="h-full w-full scale-[1.28] object-cover object-[55%_18%]"
              />
            </span>
            <h2 id="p1-vet-team-title" className="mt-3 font-fredoka text-xl font-bold text-[#1a1a1a]">
              {t('p1.vet_team_title', 'Nuestro equipo veterinario')}
            </h2>
            <p className="mt-2 font-poppins text-sm leading-snug text-black/60">
              {t(
                'p1.vet_team_body',
                'Cada receta de Milo y Lola está formulada y revisada por veterinarios especializados en nutrición canina. La ración de tu perro se calcula a medida según su peso, edad, actividad y condición corporal.',
              )}
            </p>
            {vetTeamSecondParagraph && (
              <p className="mt-2 font-poppins text-sm leading-snug text-black/60">{vetTeamSecondParagraph}</p>
            )}
            <button
              type="button"
              onClick={() => setShowVetTeam(false)}
              className="mt-5 w-full rounded-xl bg-[#008BBC] px-5 py-3 font-fredoka text-base font-semibold text-white transition-all hover:bg-[#007aa8]"
            >
              {t('p1.vet_team_close', 'Entendido')}
            </button>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
};

export default P1Welcome;
